<template>
  <main class="login" :login-state="loginState" v-if="school.school_id">
    <section>
      <div class="branding">
        <figure class="logo" alt="PTC Wizard Logo">
          <Logo />
        </figure>
        <div class="school-name">{{ schoolStore.school.school_name }}</div>
      </div>
      <div class="card">
        <form
          class="needs-validation"
          v-if="!showForgotPass"
          :class="{ 'was-validated': toggleValidation, failed: toggleFail }"
          novalidate=""
          @submit.prevent="login"
        >
          <h4 class="card-header">
            {{ isDistrict ? "District" : "School" }}
            sign in
          </h4>
          <div class="card-body">
            <div class="form-floating" field="username">
              <input
                type="text"
                class="form-control form-control-lg"
                id="floatingInput"
                :placeholder="placeholderText('username')"
                v-model="username"
                required
                data-cy="login-username"
              />
              <label for="floatingInput">{{ placeholderText("username") }}</label>
              <!-- <div class="invalid-tooltip">Username is required</div> -->
            </div>
            <div class="form-floating" field="password">
              <input
                type="password"
                class="form-control form-control-lg"
                id="floatingPassword"
                :placeholder="placeholderText('password')"
                v-model="password"
                required
                data-cy="login-password"
              />
              <label for="floatingPassword">{{ placeholderText("password") }}</label>
              <!-- <div class="invalid-tooltip">Password is required</div> -->
            </div>
            <button class="submit" :disabled="toggleSubmit" data-cy="login-submit-btn">
              <i class="bi bi-lock opacity-50"></i>
              <span>Sign in</span>
            </button>
            <div class="alert alert-danger px-5 mt-2" v-if="toggleFail">
              <!-- Login failed for some reason. Please try again, or call the school at (444) 444-4444. -->
              Authentication Failed. Please try again or contact your school for help signing in.
            </div>
            
            <div class="sso" v-if="school.integration == 'csv' || school.integration == 'none'">
              <div class="caps">Or sign in with</div>
              <div class="hstack">
                <button class="btn" @click="signinWithGoogle">
                  <LogoGoogle />
                  <span>Google</span>
                </button>
                <!-- <button class="btn">
                  <LogoMicrosoft />
                  <span>Microsoft</span>
                </button>
                <button class="btn">
                  <LogoClever />
                  <span>Clever</span>
                </button>
                <button class="btn">
                  <LogoGG4L />
                  <span>GG4L</span>
                </button> -->
              </div>
            </div>
          </div>
          <div
            class="card-footer"
            v-if="school.integration == 'csv' || school.integration == 'none'"
          >
            <button
              class="btn btn-link"
              v-if="school.integration == 'csv' || school.integration == 'none'"
              @click="showForgotPass = true"
            >
              <i class="bi bi-arrow-right me-2 opacity-50"></i><span>Forgot Password</span>
            </button>
            <!-- <router-link :to="{ name: 'CreateAccount', params: { account: $route.params.account } }" class="btn btn-link" v-if="school.integration == 'none'">
							<i class="bi bi-person-plus me-2 opacity-50"></i><span>Create Account</span>
						</router-link> -->
            <!-- <button class="btn btn-link" v-if="school.integration == 'none'"><i class="bi bi-person-plus me-2 opacity-50"></i><span>Create Account</span></button> -->
          </div>
        </form>

        <form
          class="needs-validation"
          v-if="showForgotPass"
          @submit.prevent="passwordReminder"
          :class="{ 'was-validated': toggleValidation, failed: toggleFail }"
          novalidate=""
        >
          <h4 class="card-header">Password Reminder</h4>
          <div class="card-body" v-if="!passwordReminderSent">
            <div class="form-floating" field="password">
              <input
                type="text"
                class="form-control form-control-lg"
                id="passwordReminder"
                placeholder="Enter your email address"
                v-model="passwordReminderEmail"
                required
              />
              <label for="passwordReminder">Enter your email address</label>
              <div class="invalid-tooltip">Validation Error Message</div>
            </div>
            <button class="submit" :disabled="toggleSubmit">
              <i class="bi bi-envelope opacity-50"></i>
              <span>Remind me</span>
            </button>
          </div>
          <div class="card-body" v-if="passwordReminderError">
            <p>
              <strong>{{ $t('message.Login.password_reminder_error_no_user_register', {email: passwordReminderEmail}) }}</strong>
            </p>
            
          </div>
          <div class="card-body" v-if="passwordReminderSent">
            <p>
              <strong
                >An email with further instructions has been sent to
                {{ passwordReminderEmail }}.</strong
              >
            </p>
            <p>
              The email may take a few minutes to arrive. If you don't see it in your inbox, check
              your spam folder.
            </p>
            <p v-if="schoolStore.school.school_email">
              If you are still having trouble, please contact
              {{ schoolStore.school.school_email }}
            </p>
            <p v-else>If you are still having trouble, please contact your school for help.</p>
          </div>
          <div class="card-footer">
            <button
              class="btn btn-link"
              @click="[(showForgotPass = false), (passwordReminderSent = false)]"
            >
              <i class="bi bi-arrow-left me-2 opacity-50"></i><span>Return to Sign In</span>
            </button>
          </div>
        </form>
      </div>
      <small class="text-muted vstack gap-1">
        <strong class="opacity-50">Parent Teacher Conference Scheduling Made Easy</strong>
        <div>&copy;2006 - {{ new Date().getFullYear() }} PTC Wizard, All rights reserved.</div>
      </small>
    </section>
  </main>
  <main v-else class="login loading-splash">
    <LoadingIcon />
    <span>Loading School Data…..</span>
  </main>
</template>

<script setup>
import AuthService from "@/services/auth.service";
import { useAuthenticationStore } from "@/stores/authentication";
import { useSchoolStore } from "@/stores/school";
import { ref, onMounted } from "vue";
import { useRouter } from "vue-router";
import { useRoute } from "vue-router";

import Logo from "@/assets/icons/logo-vertical-regular.svg";
import LoadingIcon from "@/assets/icons/loading.svg";

import LogoGoogle from "@/assets/brand/google.svg";
import { computed } from "vue";

const router = useRouter();
const route = useRoute();
const authenticationStore = useAuthenticationStore();
const schoolStore = useSchoolStore();
const school = ref({});
// const route = useRoute()
//   const loading = ref(true);
//   const error = ref(null);
const username = ref(null);
const password = ref(null);
const passwordReminderEmail = ref(null);
const passwordReminderSent = ref(false);
const passwordReminderError = ref(false);

const passwordError = ref(false);
const showForgotPass = ref(false);
const googleUrl = ref(null);
function passwordReminder() {
  toggleSubmit.value = true;
  toggleValidation.value = true;
  var user = {
    email: passwordReminderEmail.value,
    account: route.params.account,
    authentication_type: isDistrict.value ? "district" : "school"
  };
  AuthService.passwordReminder(user).then((result) => {
    console.log(result);
    if (result.success) {
      toggleSubmit.value = false;
      passwordReminderSent.value = true;
      passwordError.value = false;
      toggleValidation.value = false;
    } else {
      // Handle error by displaying the message
      passwordError.value = result.message;
      passwordReminderError.value=true;
      toggleSubmit.value = false;
      passwordReminderSent.value = false;
    }
  });
}

function signinWithGoogle() {
  // console.log(googleUrl.value);
  window.open(googleUrl.value);
}

function login() {
  toggleValidation.value = true;
  toggleSubmit.value = true;
  authenticationStore.login({
        username: username.value,
        password: password.value,
        account: school.value.subdomain,
        district_id: school.value.district_id
      },
      isDistrict.value
    )
    .then((response) => {
      if (response) {
        console.log("Login Successful");
        router.push(authenticationStore.getSchoolLandingPath(String(route.params.account)));
      } else {
        console.log("Login Failed");
        toggleFail.value = true;
        toggleSubmit.value = false;
      }
    });
}

function placeholderText(field) {
  if (school.value.integration == "infinitecampus" && field == "username") {
    return "Infinite Campus Username";
  } else if (school.value.integration != "infinitecampus" && field == "username") {
    return "Username";
  }
  if (school.value.integration == "infinitecampus" && field == "password") {
    return "Infinite Campus Password";
  } else if (school.value.integration != "infinitecampus" && field == "password") {
    return "Password";
  }
}
const loginState = ref("default");
const toggleValidation = ref(false);
const toggleSubmit = ref(false);
const toggleFail = ref(false);

const isDistrict = computed(
  () => schoolStore.school.is_district == "yes" || schoolStore.school.is_district == true
);

onMounted(() => {
  if (school.value.school_id) {
    authenticationStore
      .googleUrl(school.value.district_id, school.value.school_id, "parent")
      .then((response) => {
        console.log(response);
        googleUrl.value = response;
      });
    return true;
  } else {
    if (route.params.account != null && route.params.account != "") {
      schoolStore.loadSchool(route.params.account).then(() => {
        school.value = schoolStore.school;
        authenticationStore
          .googleUrl(school.value.district_id, school.value.school_id, "parent")
          .then((response) => {
            console.log(response);
            googleUrl.value = response;
          });
        console.log("School found");
      });
    } else {
      window.location.href = "https://www.ptcwizard.com";
    }
  }
});
</script>
